"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import { buildCityPriceDescription, formatRupeeFull } from "./cityPriceDescription";

type CityPriceDescriptionBlockProps = {
    model: any;
    variants: any[];
    cityName?: string;
};

export default function CityPriceDescriptionBlock({
    model,
    variants,
    cityName,
}: CityPriceDescriptionBlockProps) {
    const cityDescriptionRef = useRef<HTMLDivElement>(null);
    const [isCityDescriptionExpanded, setIsCityDescriptionExpanded] = useState(false);
    const [isCityDescriptionOverflowing, setIsCityDescriptionOverflowing] = useState(true);

    const isUpcoming = model?.status === "upcoming";
    const isElectric =
        model?.fuel_types?.some((type: string) => type?.toLowerCase() === "electric") ?? false;
    const displayCity = cityName || model?.locationInfo?.name || "";
    const modelName = model?.name || "";
    const brandNameCity = model?.brand_name || "";

    const cityPriceDescription = useMemo(
        () =>
            buildCityPriceDescription({
                model,
                variants,
                cityName: displayCity,
                isUpcoming,
                isElectric,
            }),
        [model, variants, displayCity, isUpcoming, isElectric],
    );

    const cityIntro = cityPriceDescription?.intro || "";
    const citySections = cityPriceDescription?.sections || [];
    const cityBreakupTitle = cityPriceDescription?.breakupTitle || "";
    const cityBreakupItems = cityPriceDescription?.breakupItems || [];
    const cityCompare = cityPriceDescription?.compare || "";
    const cityTableTitle = cityPriceDescription?.tableTitle || "";

    const activeCityVariants = useMemo(
        () => variants.filter((item: any) => item?.status !== "discontinued"),
        [variants],
    );

    const cityVariantPriceRows = useMemo(() => {
        if (!isCityDescriptionExpanded || !cityTableTitle) return [];
        return activeCityVariants
            .filter((item: any) => Number(item?.price) > 0)
            .sort((a: any, b: any) => Number(a.price) - Number(b.price))
            .map((item: any) => {
                const breakup = getOnRoadPrice(item, model?.locationInfo);
                const name = item?.name || "";
                const label =
                    modelName && name && !name.toLowerCase().includes(modelName.toLowerCase())
                        ? `${brandNameCity} ${modelName} ${name}`.trim()
                        : name.startsWith(brandNameCity)
                          ? name
                          : `${brandNameCity} ${name}`.trim();
                return {
                    name: label,
                    exShowroom: formatRupeeFull(Number(item?.price)),
                    insurance: formatRupeeFull(breakup?.insuranceEstimate?.total) || "-",
                    rto: formatRupeeFull(breakup?.rtoEstimate?.total) || "-",
                    price: formatRupeeFull(breakup?.onRoadPrice),
                };
            })
            .filter((row: { name: string; price: string }) => row.name && row.price);
    }, [
        isCityDescriptionExpanded,
        cityTableTitle,
        activeCityVariants,
        model?.locationInfo,
        brandNameCity,
        modelName,
    ]);

    useLayoutEffect(() => {
        const el = cityDescriptionRef.current;
        if (!el || isCityDescriptionExpanded) return;
        setIsCityDescriptionOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [cityIntro, isCityDescriptionExpanded]);

    if (!cityIntro) return null;

    return (
        <div className="text-[15px] leading-5 text-[#6B7280]">
            <div
                ref={cityDescriptionRef}
                className={isCityDescriptionExpanded ? "" : "line-clamp-2 overflow-hidden"}
            >
                <p>{cityIntro}</p>
            </div>
            {isCityDescriptionExpanded && (
                <div className="mt-3 space-y-3">
                    {citySections.map((section) => (
                        <div key={section.title}>
                            <p className="font-semibold text-[#101828]">{section.title}</p>
                            <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                {section.items.map((item) => (
                                    <li key={item.label}>
                                        <strong className="font-semibold text-[#101828]">{item.label}:</strong>{" "}
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {cityBreakupItems.length > 0 && (
                        <div>
                            <p className="font-semibold text-[#101828]">{cityBreakupTitle}</p>
                            <ul className="mt-1.5 list-disc space-y-1 pl-5">
                                {cityBreakupItems.map((item) => (
                                    <li key={item.label}>
                                        <strong className="font-semibold text-[#101828]">{item.label}:</strong>{" "}
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {cityCompare && <p>{cityCompare}</p>}
                    {cityVariantPriceRows.length > 0 && (
                        <div>
                            <p className="mb-2 font-semibold text-[#101828]">{cityTableTitle}</p>
                            <div
                                className={`overflow-x-auto rounded-xl border border-[#E8ECF0]${cityVariantPriceRows.length > 6 ? " max-h-[240px] overflow-y-auto" : ""}`}
                            >
                                <table className="w-full min-w-[640px] border-collapse">
                                    <thead className="sticky top-0 bg-[#F9F9F9]">
                                        <tr>
                                            <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[#8B93A0]">
                                                Variants
                                            </th>
                                            {["Ex-Showroom Price", "Insurance", "RTO", "On-Road Price"].map((heading) => (
                                                <th
                                                    key={heading}
                                                    className="whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.04em] text-[#8B93A0]"
                                                >
                                                    {heading}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cityVariantPriceRows.map((row) => (
                                            <tr key={row.name} className="border-t border-[#E8ECF0]">
                                                <td className="px-3 py-2.5 text-left text-[13px] font-medium text-[#101828]">
                                                    {row.name}
                                                </td>
                                                {[row.exShowroom, row.insurance, row.rto, row.price].map((value, idx) => (
                                                    <td
                                                        key={idx}
                                                        className="whitespace-nowrap px-3 py-2.5 text-right text-[13px] font-medium text-[#101828]"
                                                    >
                                                        {value}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {(isCityDescriptionOverflowing || isCityDescriptionExpanded) && (
                <button
                    type="button"
                    className="mt-2 block p-0 text-[13px] font-medium leading-5 text-[#007AFF] cursor-pointer"
                    onClick={() => setIsCityDescriptionExpanded((open) => !open)}
                >
                    {isCityDescriptionExpanded ? "Read Less" : "Read More"}
                </button>
            )}
        </div>
    );
}
